<script setup lang="ts">
import { Head } from '@inertiajs/vue3'
import { Briefcase, ChevronDown, ChevronRight } from 'lucide-vue-next'
import { ref } from 'vue'

import AppDataTable from '@/components/AppDataTable.vue'
import type { TableColumn } from '@/components/AppDataTable.vue'
import AppPagination from '@/components/AppPagination.vue'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import AppLayout from '@/layouts/AppLayout.vue'
import { formatAmount, formatDateTime } from '@/lib/utils'
import { commercialEnquiriesPath } from '@/routes'
import type { BreadcrumbItem } from '@/types'

interface CommercialEnquiry {
  id: number
  business_name: string
  contact_name: string
  email: string
  phone: string
  website: string | null
  amount: number
  years_at_business: string
  categories: string[]
  additional_info: string | null
  partner_name: string | null
  created_at: string
}

interface PaginationMeta {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

defineProps<{
  enquiries: CommercialEnquiry[]
  pagination: PaginationMeta
}>()

const breadcrumbs: BreadcrumbItem[] = [{ title: 'Commercial Enquiries', href: commercialEnquiriesPath() }]

const expandedId = ref<number | null>(null)

function toggleExpand(id: number) {
  expandedId.value = expandedId.value === id ? null : id
}

const tableColumns: TableColumn[] = [
  { key: 'expand', label: '', headerClass: 'w-8' },
  { key: 'business', label: 'Business' },
  { key: 'contact', label: 'Contact' },
  { key: 'amount', label: 'Amount' },
  { key: 'partner', label: 'Partner' },
  { key: 'received', label: 'Received' },
]
</script>

<template>
  <Head title="Commercial Enquiries" />
  <AppLayout :breadcrumbs="breadcrumbs">
    <div class="flex h-full flex-1 flex-col gap-4 overflow-x-auto p-4">
      <div class="flex items-center gap-3">
        <Briefcase class="text-muted-foreground size-6" />
        <div>
          <h1 class="text-foreground text-xl font-semibold">Commercial Enquiries</h1>
          <p class="text-muted-foreground text-sm">Enquiries submitted through the marketing website</p>
        </div>
      </div>

      <Card class="border-sidebar-border/70 dark:border-sidebar-border">
        <CardHeader class="pb-3">
          <CardTitle class="text-base">All Enquiries</CardTitle>
          <CardDescription>
            {{ pagination.total_count }} record{{ pagination.total_count !== 1 ? 's' : '' }}
          </CardDescription>
        </CardHeader>

        <CardContent class="p-0">
          <div v-if="enquiries.length === 0" class="flex flex-col items-center gap-3 py-16 text-center">
            <Briefcase class="text-muted-foreground/40 size-10" />
            <p class="text-muted-foreground text-sm">No commercial enquiries yet</p>
          </div>

          <AppDataTable v-else :columns="tableColumns" compact table-fixed>
            <template v-for="enquiry in enquiries" :key="enquiry.id">
              <tr
                class="border-border cursor-pointer border-b transition-colors select-none"
                :class="expandedId === enquiry.id ? 'bg-muted/60' : 'hover:bg-muted/40'"
                @click="toggleExpand(enquiry.id)"
              >
                <td class="px-3 py-4 text-center">
                  <ChevronDown
                    v-if="expandedId === enquiry.id"
                    class="text-muted-foreground size-4 transition-transform"
                  />
                  <ChevronRight v-else class="text-muted-foreground size-4" />
                </td>
                <td class="px-4 py-4 font-medium">{{ enquiry.business_name }}</td>
                <td class="text-muted-foreground px-4 py-4">{{ enquiry.contact_name }}</td>
                <td class="text-muted-foreground px-4 py-4">{{ formatAmount(enquiry.amount) }}</td>
                <td class="text-muted-foreground px-4 py-4">{{ enquiry.partner_name ?? '—' }}</td>
                <td class="text-muted-foreground px-4 py-4 whitespace-nowrap">
                  {{ formatDateTime(enquiry.created_at) }}
                </td>
              </tr>

              <tr v-if="expandedId === enquiry.id" class="border-border border-b">
                <td colspan="6" class="bg-muted/30 px-6 py-5">
                  <div class="flex flex-col gap-5">
                    <div class="grid grid-cols-2 gap-x-8 gap-y-3 sm:grid-cols-3 lg:grid-cols-4">
                      <div>
                        <p class="text-muted-foreground text-xs font-medium tracking-wide uppercase">Email</p>
                        <p class="mt-0.5 text-sm">{{ enquiry.email }}</p>
                      </div>
                      <div>
                        <p class="text-muted-foreground text-xs font-medium tracking-wide uppercase">Phone</p>
                        <p class="mt-0.5 text-sm">{{ enquiry.phone }}</p>
                      </div>
                      <div>
                        <p class="text-muted-foreground text-xs font-medium tracking-wide uppercase">Website</p>
                        <p class="mt-0.5 text-sm">{{ enquiry.website ?? '—' }}</p>
                      </div>
                      <div>
                        <p class="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                          Years in Business
                        </p>
                        <p class="mt-0.5 text-sm">{{ enquiry.years_at_business }}</p>
                      </div>
                      <div class="col-span-2 sm:col-span-3 lg:col-span-4">
                        <p class="text-muted-foreground text-xs font-medium tracking-wide uppercase">Categories</p>
                        <p class="mt-0.5 text-sm">{{ enquiry.categories.join(', ') }}</p>
                      </div>
                      <div v-if="enquiry.additional_info" class="col-span-2 sm:col-span-3 lg:col-span-4">
                        <p class="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                          Additional Info
                        </p>
                        <p class="mt-0.5 text-sm">{{ enquiry.additional_info }}</p>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </AppDataTable>

          <AppPagination
            v-if="pagination.total_pages > 1"
            :current-page="pagination.current_page"
            :total-pages="pagination.total_pages"
            :total-count="pagination.total_count"
            :per-page="pagination.per_page"
            :base-url="commercialEnquiriesPath()"
          />
        </CardContent>
      </Card>
    </div>
  </AppLayout>
</template>
